import { BaseCrud } from '@/shared/models/cruds.models'
import { Component, Prop, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import { GetContextMenuItemsParams, MenuItemDef } from '@ag-grid-enterprise/all-modules'
import i18n from '@/lang/i18n'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { EntityIdentifiersRequest } from '@/shared/models/http/http.model'
import {
    ConfigurationPreformattedCreateRequest,
    ConfigurationPreformattedPatchInBatchRequest,
    ConfigurationPreformattedResponse
} from '@/modules/configurationModule/detail/local-samples/preformatted/services/preformatted.dto'
import {
    initialPreformattedGridState,
    PreformattedGridState
} from '@/modules/configurationModule/detail/local-samples/preformatted/components/preformatted-grid/preformatted-grid.model'
import { preformattedGridConfigurationServices } from '@/modules/configurationModule/detail/local-samples/preformatted/services/preformatted.service'
import LocalPreformattedModal
    from '@/modules/configurationModule/detail/local-samples/preformatted/components/preformatted-modal/preformatted-modal.vue'
import { PREFORMATTED_UPDATE_CHANNEL } from '@/modules/configurationModule/detail/local-samples/preformatted/shared/models/preformatted.models'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        LocalPreformattedModal
    }
})

export default class PreformattedGrid extends BaseCrudServerSideComponent<ConfigurationPreformattedResponse> implements BaseCrud {
    @Ref() readonly localPreformattedModalWrapper!: any
    @Prop() readonly id!: number

    state: PreformattedGridState
    constructor() {
        super()
        this.state = initialPreformattedGridState()
    }

    async mounted() {
        if (this.id) {
            this.state.broadcastChannel = new BroadcastChannel(PREFORMATTED_UPDATE_CHANNEL)
            const preformattedResponse = await this.getPreformattedById(this.id)
            this.showModal(this.$tc('configuration.actions.update'), preformattedResponse, this.localPreformattedModalWrapper)
        }
    }

    beforeDestroy() {
        this.state.broadcastChannel?.close()
    }

    get broadcastChannelPostMessageFunction(): Function {
        return () => this.state.broadcastChannel?.postMessage(PREFORMATTED_UPDATE_CHANNEL)
    }

    get canDelete(): boolean {
        return !!this.state.selectedRows.length
    }

    get canUpdate(): boolean {
        return this.state.selectedRows.length === 1
    }

    getContextMenuItems(params:GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$t('configuration.actions.update').toString(),
                cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                action: () => this.onUpdate(params?.node?.data)
            },
            {
                name: this.$t('configuration.actions.delete').toString(),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: () => this.onDelete()
            },
            {
                name: params?.node?.data?.active ? this.$t('configuration.actions.deactivate').toString() : this.$t('configuration.actions.activate').toString(),
                cssClasses: this.state.selectedRows.length ? ['not-show'] : [''],
                action: () => this.activateDeactivateSelection(!params?.node?.data?.active, [params?.node?.data])
            },
            {
                name: this.$t('configuration.actions.activate').toString(),
                cssClasses: this.canActivateBatch ? [''] : ['not-show'],
                action: () => this.onActivate()
            },
            {
                name: this.$t('configuration.actions.deactivate').toString(),
                cssClasses: this.canDeactivateBatch ? [''] : ['not-show'],
                action: () => this.onDeactivate()
            }
        ]
    }

    async getPreformattedById(preformattedId: number) {
        return this.$_toCallUnique(preformattedGridConfigurationServices.getPreformattedById(this.state.labId, preformattedId))
    }

    async activateDeactivateSelection(option: boolean, selectionBatch: ConfigurationPreformattedResponse[]): Promise<void> {
        const filteredRowActiveState = selectionBatch?.filter((row: ConfigurationPreformattedResponse) => row.active !== option)
        const updatedFilteredRowActiveState: ConfigurationPreformattedPatchInBatchRequest[] = filteredRowActiveState?.map((row: ConfigurationPreformattedResponse) => {
            return {
                id: row.id!,
                active: option
            }
        })
        if (updatedFilteredRowActiveState?.length) {
            this.$_updateLoading(true)
            const [err] = await this.$_toCall(preformattedGridConfigurationServices.patchPreformatted(this.state.labId, updatedFilteredRowActiveState), true)
            this.$_updateLoading(false)
            if (err) {
                return
            }
            if (selectionBatch.length > 1) {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 2, { elements: selectionBatch.length }))
            } else {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
            }
            this.reloadData()
        }
    }

    async createOperation(row: ConfigurationPreformattedCreateRequest): Promise<void> {
        this.$_updateLoading(true)
        const [err, response] = await this.$_toCall(preformattedGridConfigurationServices.createPreformatted(this.state.labId, row), true)
        this.$_updateLoading(false)
        if (err) {
            return
        } else if (response?.length) {
            this.$_handleErrors(response)
            return
        }
        this.localPreformattedModalWrapper.$slots.default[0].componentInstance.updateRow(response)
        this.state = {
            ...this.state,
            actionTitle: i18n.t('configuration.actions.update').toString()
        }
        this.$_showToastSuccess(this.$t('configuration.toastMessages.created')?.toString())
        this.reloadData()
    }

    async patchOperation(rows: ConfigurationPreformattedPatchInBatchRequest[]): Promise<void> {
        this.$_updateLoading(true)
        const [err, response] = await this.$_toCall(preformattedGridConfigurationServices.patchPreformatted(this.state.labId, rows), true)
        this.$_updateLoading(false)
        if (err) {
            return
        } else if (response?.length) {
            this.$_handleErrors(response)
            return
        }
        this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
        this.reloadData()
    }

    async patchOperationWithModal(preformatted: ConfigurationPreformattedPatchInBatchRequest[]): Promise<void> {
        await this.patchOperation(preformatted)
        if (this.state.broadcastChannel) {
            this.broadcastChannelPostMessageFunction()
        }
    }

    async deleteOperation(rows: ConfigurationPreformattedResponse[]): Promise<void> {
        if (rows.length) {
            const selectedRowsSize:number = this.state?.selectedRows?.length
            const labelConfirmation:string = this.$t('configuration.grid.deleteConfirmation')?.toString()
            const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
            const labelDeleteQuestion:string = this.$t('configuration.grid.deleteQuestion')?.toString()
            const labelDeleteAction:string = this.$t('configuration.actions.delete')?.toString()
            const promise = await this.$_showConfirmationModal(
                `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelDeleteQuestion}`,
                labelDeleteAction
            )
            if (promise) {
                this._deleteBatchOperation(rows)
            }
        }
    }

    onAdd(): void {
        this.showModal(this.$t('configuration.actions.add').toString(), null, this.localPreformattedModalWrapper)
    }

    onUpdate(targetResponseTime: ConfigurationPreformattedResponse = this.state.gridApi?.getSelectedRows()[0]): void {
        if (targetResponseTime) {
            this.showModal(this.$t('configuration.actions.update').toString(), targetResponseTime, this.localPreformattedModalWrapper)
        }
    }

    onDelete(): void {
        if (this.state.selectedRows.length) {
            this.deleteOperation(this.state.selectedRows)
        }
    }

    onActivate(): void {
        this.activateDeactivateSelection(true, this.state.selectedRows)
    }

    onDeactivate(): void {
        this.activateDeactivateSelection(false, this.state.selectedRows)
    }

    private async _deleteBatchOperation(rows: ConfigurationPreformattedResponse[]): Promise<void> {
        const rowsToDelete: EntityIdentifiersRequest = {
            entityIdentifiers: rows.map((template: ConfigurationPreformattedResponse) => Number(template?.id))
        }
        this.$_updateLoading(true)
        const [err, response] = await this.$_toCall(preformattedGridConfigurationServices.deletePreformatted(this.state.labId, rowsToDelete), true)
        this.$_updateLoading(false)
        if (err) {
            return
        } else if (response.length) {
            this.$_handleErrors(response)
            return
        }
        if (rows.length > 1) {
            this.$_showToastSuccess(
                this.$tc('configuration.toastMessages.deleted', 2, { elements: rows?.length })
            )
        } else {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', 1))
        }
        this.reloadData()
    }
}
